import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { NavigationStart, Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { TranslateService } from '@ngx-translate/core';
import 'rxjs/add/operator/filter';
import {
  CeoClothingLossDecisionPayload,
  CeoPendingLossCase,
  HeadClothingLossDecisionPayload,
  HeadPendingLossCase
} from '../models/clothing.model';
import { ClothingHeadLossDecisionModalComponent } from '../modals/clothing-head-loss-decision-modal.component';
import { ToastService } from './toast.service';
import { UserService } from './user.service';
import { ClothingErrorService } from './clothing-error.service';
import { ClothingService } from './clothing.service';
import {
  AppNotification,
  ClothingNotificationRoutingService
} from './clothing-notification-routing.service';

const REQUEST_CREATED_TYPES = ['REQUEST_CREATED', 'STOREKEEPER_NEW_REQUEST'];
const HEAD_LOSS_NOTIFICATION_TYPES = [
  'LOSS_DECLARED',
  'HEAD_NEW_LOSS_CASE',
  'HEAD_LOSS_DECISION_REQUIRED'
];
const CEO_LOSS_NOTIFICATION_TYPES = ['CEO_LOSS_DECISION_REQUIRED'];
const ROUTE_CHANGE_DISMISS_REASON = 'clothing notification route change';
type ClothingPendingLossCase = HeadPendingLossCase | CeoPendingLossCase;

@Injectable()
export class ClothingNotificationActionService {
  private activeNotificationKey = '';
  private activeModalRef: NgbModalRef | null = null;

  constructor(
    private modalService: NgbModal,
    private router: Router,
    private translate: TranslateService,
    private toastService: ToastService,
    private userService: UserService,
    private clothingErrorService: ClothingErrorService,
    private clothingService: ClothingService,
    private clothingNotificationRoutingService: ClothingNotificationRoutingService
  ) {
    this.dismissActiveModalOnNavigation();
  }

  openNotification(notification: AppNotification): Promise<boolean> {
    if (this.isViewedNotification(notification)) {
      return Promise.resolve(false);
    }
    if (this.hasActiveNotificationAction()) {
      return Promise.resolve(false);
    }
    this.activeNotificationKey = this.resolveNotificationActionKey(notification);
    return this.openNotificationAction(notification).then(
      (opened) => this.completeNotificationAction(opened),
      () => this.completeNotificationAction(false)
    );
  }

  private openNotificationAction(notification: AppNotification): Promise<boolean> {
    if (this.shouldOpenHeadLossDecision(notification)) {
      return this.openHeadLossDecision(notification);
    }
    if (this.shouldOpenCeoLossDecision(notification)) {
      return this.openCeoLossDecision(notification);
    }
    return this.openProfile(notification);
  }

  private shouldOpenHeadLossDecision(notification: AppNotification): boolean {
    return this.hasHeadRole() && !!notification && !!notification.lossCaseId
      && this.isHeadLossNotification(notification);
  }

  private shouldOpenCeoLossDecision(notification: AppNotification): boolean {
    if (!this.hasAdminRole() || !notification || !notification.lossCaseId) {
      return false;
    }
    return this.isCeoLossNotification(notification);
  }

  private openHeadLossDecision(notification: AppNotification): Promise<boolean> {
    return new Promise((resolve) => {
      this.clothingService.getHeadPendingLossCases().subscribe(
        (lossCases) => this.openHeadLossModal(notification, lossCases, resolve),
        (error) => this.handleActionError(error, 'CLOTHING_LOSS_DECISION_ERROR', resolve)
      );
    });
  }

  private openHeadLossModal(
    notification: AppNotification,
    lossCases: HeadPendingLossCase[],
    resolve: (value: boolean) => void
  ): void {
    var lossCase = this.findPendingLossCase(lossCases, notification.lossCaseId);

    if (!lossCase) {
      this.handleMissingLossCase(resolve);
      return;
    }

    var modalRef = this.modalService.open(ClothingHeadLossDecisionModalComponent);
    this.trackActiveModal(modalRef);
    modalRef.componentInstance.setHeadValues(lossCase);
    modalRef.result.then(
      (payload) => this.submitHeadLossDecision(lossCase, payload, resolve),
      () => resolve(false)
    );
  }

  private submitHeadLossDecision(
    lossCase: HeadPendingLossCase,
    payload: HeadClothingLossDecisionPayload,
    resolve: (value: boolean) => void
  ): void {
    if (!payload) {
      resolve(false);
      return;
    }
    this.clothingService.decideHeadLossCase(lossCase.lossCaseId, payload).subscribe(
      () => this.handleSuccess('CLOTHING_LOSS_DECISION_SUCCESS', resolve),
      (error) => this.handleActionError(error, 'CLOTHING_LOSS_DECISION_ERROR', resolve)
    );
  }

  private openCeoLossDecision(notification: AppNotification): Promise<boolean> {
    return new Promise((resolve) => {
      this.clothingService.getCeoPendingLossCases().subscribe(
        (lossCases) => this.openCeoLossModal(notification, lossCases, resolve),
        (error) => this.handleActionError(error, 'CLOTHING_CEO_LOSS_DECISION_ERROR', resolve)
      );
    });
  }

  private openCeoLossModal(
    notification: AppNotification,
    lossCases: CeoPendingLossCase[],
    resolve: (value: boolean) => void
  ): void {
    var lossCase = this.findPendingLossCase(lossCases, notification.lossCaseId);

    if (!lossCase) {
      this.handleMissingCeoLossCase(resolve);
      return;
    }

    var modalRef = this.modalService.open(ClothingHeadLossDecisionModalComponent);
    this.trackActiveModal(modalRef);
    modalRef.componentInstance.setCeoValues(lossCase);
    modalRef.result.then(
      (payload) => this.submitCeoLossDecision(lossCase, payload, resolve),
      () => resolve(false)
    );
  }

  private submitCeoLossDecision(
    lossCase: CeoPendingLossCase,
    payload: CeoClothingLossDecisionPayload,
    resolve: (value: boolean) => void
  ): void {
    if (!payload) {
      resolve(false);
      return;
    }
    this.clothingService.decideCeoLossCase(lossCase.lossCaseId, payload).subscribe(
      () => this.handleSuccess('CLOTHING_CEO_LOSS_DECISION_SUCCESS', resolve),
      (error) => this.handleActionError(error, 'CLOTHING_CEO_LOSS_DECISION_ERROR', resolve)
    );
  }

  private handleSuccess(messageKey: string, resolve: (value: boolean) => void): void {
    this.toastService.success(this.translate.instant(messageKey));
    resolve(true);
  }

  private handleActionError(
    error: HttpErrorResponse,
    fallbackKey: string,
    resolve: (value: boolean) => void
  ): void {
    this.toastService.error(this.resolveErrorMessage(error, fallbackKey));
    resolve(false);
  }

  private handleMissingLossCase(resolve: (value: boolean) => void): void {
    this.toastService.error(this.translate.instant('CLOTHING_PENDING_LOSS_NOT_FOUND'));
    resolve(false);
  }

  private handleMissingCeoLossCase(resolve: (value: boolean) => void): void {
    this.toastService.error(this.translate.instant('CLOTHING_CEO_PENDING_LOSS_NOT_FOUND'));
    resolve(false);
  }

  private openProfile(notification: AppNotification): Promise<boolean> {
    var route = this.clothingNotificationRoutingService.resolveProfileRoute(
      notification,
      this.userService.getLocal().id
    );

    this.router.navigate([route]);
    return Promise.resolve(true);
  }

  private findPendingLossCase<T extends ClothingPendingLossCase>(
    lossCases: T[],
    lossCaseId: string
  ): T {
    if (!lossCases || !lossCases.length) {
      return null;
    }
    return lossCases.find((lossCase) => lossCase.lossCaseId === lossCaseId);
  }

  private isRequestCreatedNotification(notification: AppNotification): boolean {
    return this.isNotificationType(notification, REQUEST_CREATED_TYPES);
  }

  private isHeadLossNotification(notification: AppNotification): boolean {
    return this.isNotificationType(notification, HEAD_LOSS_NOTIFICATION_TYPES);
  }

  private isCeoLossNotification(notification: AppNotification): boolean {
    return this.isNotificationType(notification, CEO_LOSS_NOTIFICATION_TYPES);
  }

  private isNotificationType(notification: AppNotification, types: string[]): boolean {
    return types.indexOf(this.resolveNotificationType(notification)) !== -1;
  }

  private resolveNotificationType(notification: AppNotification): string {
    if (notification && notification.clothingNotificationType) {
      return notification.clothingNotificationType;
    }
    if (notification && notification.type) {
      return notification.type;
    }
    return '';
  }

  private isViewedNotification(notification: AppNotification): boolean {
    return !!notification && notification.viewed === true;
  }

  private hasActiveNotificationAction(): boolean {
    return !!this.activeNotificationKey;
  }

  private resolveNotificationActionKey(notification: AppNotification): string {
    if (notification && notification.id) {
      return notification.id;
    }
    if (notification && notification.lossCaseId) {
      return notification.lossCaseId;
    }
    return 'unknown-clothing-notification';
  }

  private completeNotificationAction(opened: boolean): boolean {
    this.activeNotificationKey = '';
    return opened;
  }

  private dismissActiveModalOnNavigation(): void {
    this.router.events
      .filter(event => event instanceof NavigationStart)
      .subscribe(() => this.dismissActiveModal());
  }

  private trackActiveModal(modalRef: NgbModalRef): void {
    this.activeModalRef = modalRef;
    modalRef.result.then(
      () => this.clearActiveModal(modalRef),
      () => this.clearActiveModal(modalRef)
    );
  }

  private dismissActiveModal(): void {
    if (!this.activeModalRef) {
      return;
    }
    var modalRef = this.activeModalRef;
    this.activeModalRef = null;
    modalRef.dismiss(ROUTE_CHANGE_DISMISS_REASON);
  }

  private clearActiveModal(modalRef: NgbModalRef): void {
    if (this.activeModalRef !== modalRef) {
      return;
    }
    this.activeModalRef = null;
  }

  private hasHeadRole(): boolean {
    return this.hasRole('ROLE_HEAD');
  }

  private hasAdminRole(): boolean {
    return this.hasRole('ROLE_ADMIN');
  }

  private hasRole(role: string): boolean {
    var user = this.userService.getLocal();

    if (!user || !user.roles) {
      return false;
    }
    return user.roles.indexOf(role) !== -1;
  }

  private resolveErrorMessage(error: HttpErrorResponse, fallbackKey: string): string {
    return this.clothingErrorService.resolveMessage(error, fallbackKey);
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/services/clothing-notification-action.service.ts